
import {
  defineComponent, PropType, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { ShopsManufacturers } from '@/types/generated-types/graphql';
import InputText from 'primevue/inputtext';
import ShopsManufacturersList from '@/composables/shops-manufacturers/ShopsManufacturers';

const PAGE_SIZE = 50;

export default defineComponent({
  name: 'SupplierSelection',

  components: {
    Dialog,
    Button,
    InputText,
  },

  props: {
    availableManufacturersArr: {
      type: Array as PropType<ShopsManufacturers[]>,
      required: true,
    },

    chosenManufacturersArr: {
      type: Array as PropType<ShopsManufacturers[]>,
      required: true,
    },

    manufacturerSearchStr: {
      type: String,
      required: true,
    },
  },

  emits: ['close-action-modal'],

  setup(props, context) {
    const { t } = useI18n();
    const dialogVisible = ref(true);
    const supplier = ref(props.manufacturerSearchStr);
    const suppliersAvailableList = ref<ShopsManufacturers[]>([...props.availableManufacturersArr]);
    const suppliersChosenList = ref<ShopsManufacturers[]>([...props.chosenManufacturersArr]);
    // Stocker les valeurs initiales pour annulation
    const initialChosenList = [...props.chosenManufacturersArr];
    const availableListRef = ref<HTMLElement | null>(null);

    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const hasMoreResults = ref(true);
    const currentOffset = ref(0);
    let searchTimer: ReturnType<typeof setTimeout> | null = null;
    let searchRequestId = 0;

    const handleCancel = () => {
      dialogVisible.value = false;
      // Retourner les valeurs initiales
      context.emit('close-action-modal', initialChosenList, props.availableManufacturersArr, props.manufacturerSearchStr);
    };

    const handleValidate = () => {
      dialogVisible.value = false;
      context.emit('close-action-modal', suppliersChosenList.value, suppliersAvailableList.value, supplier.value);
    };

    const updateDialogVisibility = (visible: boolean) => {
      if (!visible) {
        // Fermeture via croix = annulation
        handleCancel();
      }
    };

    const filterOutChosen = (suppliers: ShopsManufacturers[]): ShopsManufacturers[] => suppliers.filter(
      (s) => !suppliersChosenList.value.some((chosen) => chosen.id_shop_manufacturer === s.id_shop_manufacturer),
    );

    const handleSearch = async () => {
      if (searchTimer) clearTimeout(searchTimer);

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      const searchTerm = supplier.value.trim();
      if (!searchTerm) {
        suppliersAvailableList.value = [];
        isLoading.value = false;
        return;
      }

      searchTimer = setTimeout(async () => {
        if (currentRequestId !== searchRequestId) return;

        isLoading.value = true;
        currentOffset.value = 0;
        hasMoreResults.value = true;
        suppliersAvailableList.value = [];

        const result = await ShopsManufacturersList(supplier.value, PAGE_SIZE, 0);

        if (result === null || currentRequestId !== searchRequestId) return;

        suppliersAvailableList.value = filterOutChosen(result);
        hasMoreResults.value = result.length === PAGE_SIZE;
        currentOffset.value = PAGE_SIZE;
        isLoading.value = false;
      }, 500);
    };

    const loadMore = async () => {
      if (isLoadingMore.value || !hasMoreResults.value) return;

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      isLoadingMore.value = true;
      const result = await ShopsManufacturersList(supplier.value, PAGE_SIZE, currentOffset.value);

      if (result === null || currentRequestId !== searchRequestId) {
        isLoadingMore.value = false;
        return;
      }

      if (result.length > 0) {
        const newSuppliers = filterOutChosen(result);
        suppliersAvailableList.value = [...suppliersAvailableList.value, ...newSuppliers];
        hasMoreResults.value = result.length === PAGE_SIZE;
        currentOffset.value += PAGE_SIZE;
      } else {
        hasMoreResults.value = false;
      }
      isLoadingMore.value = false;
    };

    const handleAvailableScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMore();
      }
    };

    const selectItem = (supplierItem: ShopsManufacturers) => {
      suppliersChosenList.value.unshift(supplierItem);
      suppliersAvailableList.value = suppliersAvailableList.value.filter(
        (s) => s.id_shop_manufacturer !== supplierItem.id_shop_manufacturer,
      );
    };

    const removeItem = (supplierItem: ShopsManufacturers) => {
      suppliersChosenList.value = suppliersChosenList.value.filter(
        (s) => s.id_shop_manufacturer !== supplierItem.id_shop_manufacturer,
      );
    };

    return {
      t,
      dialogVisible,
      updateDialogVisibility,
      handleCancel,
      handleValidate,
      supplier,
      handleSearch,
      suppliersAvailableList,
      suppliersChosenList,
      availableListRef,
      selectItem,
      removeItem,
      handleAvailableScroll,
      isLoading,
      isLoadingMore,
      hasMoreResults,
    };
  },
});
